<template>
  <div class="article-page">
    <Hero 
      :title="articleMetadata.title" 
      :imageUrl="articleMetadata.image"
      height="50vh"
    />
    <article class="article-content">
      <div class="container">
        <div class="article-header">
          <h1 class="article-title">{{ articleMetadata.title }}</h1>
          <div class="article-meta" v-if="articleMetadata.date">
            <span class="article-date">{{ formattedDate }}</span>
            <span class="article-category">{{ $route.params.section }}</span>
          </div>
        </div>
        <div class="content" v-html="articleContent"></div>
        <div class="article-footer">
          <router-link :to="`/${$route.params.section}`" class="btn btn-ghost">
            ← Back to {{ capitalizedSection }}
          </router-link>
        </div>
      </div>
    </article>
  </div>
</template>

<script>
import Hero from '@/components/Hero.vue';
import markdownIt from 'markdown-it';
import markdownItForInline from 'markdown-it-for-inline';
import { logError } from '@/utils/logger';

export default {
  name: 'Article',
  components: {
    Hero,
  },
  data() {
    return {
      articleMetadata: {},
      articleContent: '',
    };
  },
  computed: {
    formattedDate() {
      if (!this.articleMetadata.date) return '';
      const date = new Date(this.articleMetadata.date);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    capitalizedSection() {
      return this.$route.params.section.charAt(0).toUpperCase() + this.$route.params.section.slice(1);
    }
  },
  async created() {
    const section = this.$route.params.section;
    const articleId = this.$route.params.id;

    // Import metadata
    try {
      const metaModule = await import(`@/content/${section}/${articleId}/metadata.json`);
      this.articleMetadata = metaModule.default;
    } catch (error) {
      this.$router.push('/');
    }

    // Import markdown content
    try {
      const contentModule = await import(`@/content/${section}/${articleId}/index.md`);
      const mdContent = contentModule.default;

      const md = markdownIt();

      // Use markdown-it-for-inline to process inline elements
      md.use(markdownItForInline, 'image_handler', 'image', (tokens, idx) => {
        const token = tokens[idx];
        const srcIndex = token.attrIndex('src');
        let src = token.attrs[srcIndex][1];

        // Handle '@/' alias in image paths
        if (src.startsWith('@/')) {
          // Replace '@/' with '/src/'
          src = src.replace('@/', '/src/');

          // Use require to resolve the image path
          try {
            const resolvedSrc = require(`${src}`);
            token.attrs[srcIndex][1] = resolvedSrc;
          } catch (e) {
            logError(`Image not found: ${src}`, e);
            // Optionally, set a placeholder image or handle the error as needed
          }
        }
      });

      // Render the markdown content
      this.articleContent = md.render(mdContent);
    } catch (error) {
      this.$router.push('/');
    }
  },
};
</script>

<style scoped>
.article-content {
  padding: var(--space-16) 0;
  max-width: 900px;
  margin: 0 auto;
}

.article-header {
  text-align: center;
  margin-bottom: var(--space-16);
  padding-bottom: var(--space-8);
  border-bottom: 1px solid var(--neutral-300);
}

.article-title {
  font-family: var(--font-display);
  font-size: var(--text-4xl);
  font-weight: 700;
  color: var(--neutral-900);
  margin-bottom: var(--space-6);
  line-height: var(--leading-tight);
}

.article-meta {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--space-6);
  font-size: var(--text-sm);
  color: var(--neutral-500);
}

.article-date {
  font-weight: 500;
}

.article-category {
  padding: var(--space-1) var(--space-3);
  background: var(--primary-color);
  color: var(--neutral-100);
  border-radius: var(--radius-full);
  font-weight: 600;
  text-transform: capitalize;
  font-size: var(--text-xs);
}

.article-footer {
  margin-top: var(--space-16);
  padding-top: var(--space-8);
  border-top: 1px solid var(--neutral-300);
  text-align: center;
}

/* Ensure content has proper container styling */
.content {
  max-width: 800px;
  margin: 0 auto;
}

/* Responsive Design */
@media (max-width: 768px) {
  .article-content {
    padding: var(--space-12) 0;
  }
  
  .article-title {
    font-size: var(--text-3xl);
  }
  
  .article-header {
    margin-bottom: var(--space-12);
  }
  
  .article-meta {
    flex-direction: column;
    gap: var(--space-3);
  }
}

@media (max-width: 480px) {
  .article-title {
    font-size: var(--text-2xl);
  }
  
  .article-header {
    margin-bottom: var(--space-8);
    padding-bottom: var(--space-6);
  }
  
  .article-footer {
    margin-top: var(--space-12);
    padding-top: var(--space-6);
  }
}
</style>
